import { Link } from "react-router-dom";
import { GlowOrb } from "@/components/common/GlowOrb";
import { Calendar } from "lucide-react";
import Seo from "@/components/seo/Seo";
import { usePublishedArticles } from "@/data/articleRepo";

export default function Blog() {
  const { items, loading } = usePublishedArticles();

  return (
    <div data-testid="blog-page">
      <Seo
        title="Insights & Blog"
        description="AI, software engineering and enterprise product insights from CloudLab Technologies."
        keywords="AI blog, software development blog, engineering insights, CloudLab blog"
      />
      <section className="relative overflow-hidden py-20 md:py-28">
        <GlowOrb className="top-0 left-0" color="accent" size={500} />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-4">Insights</div>
          <h1 className="font-heading font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
            Field notes from the <span className="text-gradient-aurora">frontier of AI.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            Long-form strategy, architecture and product thinking — written by our engineers and strategists.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6">
          {loading && items.length === 0 ? (
            <div className="grid md:grid-cols-3 gap-4">
              {[0,1,2,3,4,5].map(i => <div key={i} className="h-72 glass rounded-3xl animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="glass rounded-3xl p-16 text-center">
              <div className="font-heading font-bold text-2xl">No articles yet</div>
              <p className="mt-3 text-muted-foreground">Our publishing engine is warming up. Check back soon, or subscribe below.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((p) => (
                <Link
                  to={`/blog/${p.slug}`}
                  key={p.slug}
                  className="group glass rounded-3xl overflow-hidden hover:border-primary/50 transition-colors"
                  data-testid={`blog-card-${p.slug}`}
                >
                  {p.heroImage ? (
                    <div className="aspect-[16/9] overflow-hidden bg-gradient-to-br from-primary/20 via-secondary/10 to-primary/5">
                      <img
                        src={p.heroImage} alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-primary/25 via-secondary/15 to-primary/5" />
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em] text-primary">
                      <span>{p.category}</span>
                      {p.publishedAt && <>
                        <span>·</span>
                        <span className="text-muted-foreground inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(p.publishedAt).toLocaleDateString()}</span>
                      </>}
                    </div>
                    <h3 className="mt-3 font-heading font-bold text-xl leading-tight group-hover:text-primary">{p.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
